// ─────────────────────────────────────────────────────────────────────────
// headerMeta — how ONE entry of a detail header's `meta` row is resolved.
//
// A meta entry used to be nothing but `{ icon, text }`: a finished string the
// module's header config had already glued together ("Candidate rate: $100").
// That made two things impossible, and both were reported as bugs:
//
//  1. A label that depends on the record. A C2C submission's rate belongs to
//     the employer, so the header must read "Employer rate" — exactly the
//     switch the FORM already performs through `field.labelWhen`. With the
//     label baked into a template string the header could only ever say one
//     thing, so it said the wrong one for half the records.
//  2. A meta entry that is a STATE, not a sentence. "Internal status: Shortlisted"
//     is a status and must read like one (a coloured pill), not like body text.
//
// So a meta entry may now describe itself instead of pre-rendering itself:
//
//   { label, value }        → rendered as "label: value" (text as before)
//   { labelWhen: [rule] }   → label swapped when the rule matches the record;
//                             SAME rule shape and matcher as showIf/labelWhen
//                             in the form engine (one operator table, both
//                             surfaces), so admin config transfers verbatim.
//   { variant: 'pill', tone } → rendered through the status pill's own styling
//   { valueStyles: [...] }  → colours from the admin field config, the SAME
//                             array shape ListView's badge engine reads, so
//                             recolouring a status stays admin work
//
// `text` still wins when a config supplies it, so every existing header config
// keeps rendering byte-identically.
// ─────────────────────────────────────────────────────────────────────────

import { conditionMatches } from '../form/conditionalFieldLabel';

const isEmpty = (v) => v === undefined || v === null || v === '';

// Antd Tag colour keywords a `tone` maps onto. Tones — not colours — are what a
// config declares, so the palette can change in one place.
export const META_TONE_COLORS = {
  positive: 'success',
  success: 'success',
  negative: 'error',
  error: 'error',
  warning: 'warning',
  info: 'processing',
  neutral: 'default',
  default: 'default',
};

function ruleList(raw) {
  if (!raw) return [];
  return (Array.isArray(raw) ? raw : [raw]).filter((rule) => rule?.field && rule?.label);
}

/**
 * resolveMetaLabel — the label this entry should carry for THIS record.
 *
 * FAIL CLOSED, exactly like detailFieldLabel: a rule whose field is absent from
 * the record cannot be judged, so it is skipped and the static label kept.
 * Asserting "Employer rate" on an unproven condition would mislabel real money.
 */
export function resolveMetaLabel(item, readValue) {
  const read = typeof readValue === 'function' ? readValue : () => undefined;
  for (const rule of ruleList(item?.labelWhen)) {
    const value = read(rule.field);
    if (value === undefined) continue;
    if (conditionMatches(rule, value)) return rule.label;
  }
  return item?.label ?? '';
}

/**
 * metaValueStyle — the admin colour for this entry's value, matched
 * case-insensitively on value OR label. Mirrors ListView.getBadgeValueStyle and
 * financeUtils.getValueStyle so one seeded `valueStyles` array colours the list
 * cell, the finance table and this header pill identically.
 */
export function metaValueStyle(item) {
  const styles = item?.valueStyles;
  if (!Array.isArray(styles) || !styles.length) return null;
  const needle = String(item?.value ?? item?.text ?? '').trim().toLowerCase();
  if (!needle) return null;
  const match = styles.find((style) => (
    String(style?.value ?? '').trim().toLowerCase() === needle
    || String(style?.label ?? '').trim().toLowerCase() === needle
  ));
  if (!match) return null;
  return {
    color: match.color || undefined,
    bg: match.bg ?? match.background ?? match.backgroundColor ?? undefined,
  };
}

/**
 * resolveMetaItem — everything the renderer needs for one entry:
 *   { icon, text, variant, tagColor, tagStyle }
 *
 * `readValue(fieldKey)` is supplied by the renderer (the flattened record on the
 * detail header), so this module stays free of any module/field knowledge.
 */
export function resolveMetaItem(item, readValue) {
  const label = resolveMetaLabel(item, readValue);
  const value = item?.value;
  // A config that already produced its own sentence keeps it; otherwise the
  // label and value are joined here, and ONLY when both exist — a value with no
  // label must never render as ": Shortlisted".
  const text = !isEmpty(item?.text)
    ? String(item.text)
    : [label, isEmpty(value) ? '' : String(value)].filter(Boolean).join(label && !isEmpty(value) ? ': ' : '');

  const variant = item?.variant === 'pill' ? 'pill' : 'text';
  if (variant !== 'pill') return { icon: item?.icon, text, variant };

  const style = metaValueStyle(item);
  return {
    icon: item?.icon,
    // A pill shows the STATE; the label rides along as the pill's prefix only
    // when the config asked for one via `pillLabel` (default: label + value, so
    // "Internal status: Shortlisted" still reads as a labelled state).
    text,
    label,
    value: isEmpty(value) ? '' : String(value),
    variant,
    // Explicit admin colours win over the tone keyword; with neither, the pill
    // renders in antd's neutral default rather than guessing a meaning.
    tagColor: style?.color || style?.bg ? undefined : (META_TONE_COLORS[item?.tone] ?? 'default'),
    tagStyle: style?.color || style?.bg
      ? { color: style.color, backgroundColor: style.bg, borderColor: style.bg ?? style.color }
      : undefined,
  };
}

export default resolveMetaItem;
